import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, output, signal } from '@angular/core';
import { ScheduleChange, ScheduleExceptions } from '@jms/model/scheduling';
import { ScheduleRule } from '@jms/model/scheduling/schedule.rule';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { CalendarDate, CalendarRange, MiniCalendarComponent, MiniCalendarMode } from '@workbuddy/ui/v2/components/calendar';
import { SCHEDULE_ENGINE, SCHEDULE_TODAY, ScheduleCardContext, buildScheduleContext, formatLongDate, joinDates } from '../card/schedule.card.model';
import { exceptionPopoverBridgeStyles, exceptionPopoverStyleId } from './exception.popover.theme';

/** Which exception the panel authors. The two share every piece of chrome and differ only in what they ask for. */
export enum ExceptionPopoverMode {
  Skip = 'Skip',
  Extra = 'Extra',
}

/**
 * Wording overrides for a caller that authors the same shape under a different name — a plan or line
 * blackout is a skip range with a reason, but calling it "Skip dates" on a plan screen would tell the
 * user they are editing a task. Only the words move; the panel, the calendar, and the emitted change
 * are identical, which is the point of overriding rather than forking the component.
 */
export interface ExceptionPopoverLabels {
  readonly title?: string;
  readonly note?: string;
  readonly confirm?: string;
}

/**
 * The Skip dates / Add an extra date panel — one component because the two boards are the same panel
 * with a different question: a range and a reason, or a single day and a note.
 *
 * The impact note is the whole point of the panel. It runs the real engine over the real exception set
 * before anything is committed, so the user reads "Skips 2 planned services" rather than discovering it
 * after saving. That means the panel needs the task's schedule and its current exceptions, not just a
 * date — it emits a `ScheduleChange` and lets the card apply it.
 */
@Component({
  selector: 'wb-schedule-exception-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent, MiniCalendarComponent],
  templateUrl: 'exception.popover.component.html',
  styleUrls: ['./exception.popover.component.less'],
})
export class ScheduleExceptionPopoverComponent {

  private readonly document = inject(DOCUMENT);

  private readonly todayProvider = inject(SCHEDULE_TODAY);

  public readonly $mode = input.required<ExceptionPopoverMode>({ alias: 'mode' });

  /** The task's pattern. Without one there is nothing to count an impact against. */
  public readonly $schedule = input<ScheduleRule | null>(null, { alias: 'schedule' });

  /** Exceptions already on the task; the impact preview subtracts them before counting. */
  public readonly $exceptions = input<ScheduleExceptions | undefined>(undefined, { alias: 'exceptions' });

  public readonly $context = input<ScheduleCardContext | null>(null, { alias: 'context' });

  /** Overrides the title / note label / confirm text. Absent keys keep the mode's own wording. */
  public readonly $labels = input<ExceptionPopoverLabels | null>(null, { alias: 'labels' });

  public readonly changed = output<ScheduleChange>();

  public readonly cancelled = output<void>();

  protected readonly ExceptionPopoverMode = ExceptionPopoverMode;

  protected readonly MiniCalendarMode = MiniCalendarMode;

  protected readonly buttonSize = ButtonSize.Medium;

  protected readonly secondaryStyle = ButtonDisplayStyle.Secondary;

  protected readonly primaryStyle = ButtonDisplayStyle.Primary;

  protected readonly today = this.todayProvider();

  /** Range endpoints in Skip mode. Seeded to today so the panel opens on a usable month, not a blank grid. */
  protected readonly $range = signal<CalendarRange>({ from: this.today, to: this.today });

  /** The single day in Extra mode. */
  protected readonly $date = signal<CalendarDate>(this.today);

  protected readonly $isSkip = computed(() => this.$mode() === ExceptionPopoverMode.Skip);

  protected readonly $title = computed(() => this.$labels()?.title ?? (this.$isSkip() ? 'Skip dates' : 'Add an extra date'));

  protected readonly $noteLabel = computed(() => this.$labels()?.note ?? (this.$isSkip() ? 'Reason' : 'Note'));

  protected readonly $confirmLabel = computed(() => this.$labels()?.confirm ?? (this.$isSkip() ? 'Skip these dates' : 'Add date'));

  protected readonly $fromLabel = computed(() => formatLongDate(this.$range().from ?? this.today));

  protected readonly $toLabel = computed(() => formatLongDate(this.$range().to ?? this.$range().from ?? this.today));

  protected readonly $dateLabel = computed(() => formatLongDate(this.$date()));

  protected readonly $note = signal<string>('');

  /**
   * The dates this change actually removes. Skip mode asks the engine which occurrences fall in the
   * chosen range; Extra mode adds exactly one, so there is nothing to compute.
   */
  protected readonly $impactDates = computed<CalendarDate[]>(() => {

    if (!this.$isSkip()) return [this.$date()];

    const schedule = this.$schedule();
    const range = this.$range();
    if (!schedule || !range.from) return [];

    const context = buildScheduleContext(this.today, this.$context());

    return SCHEDULE_ENGINE.occurrencesBetween(schedule, range.from, range.to ?? range.from, context, this.$exceptions());
  });

  protected readonly $impactNote = computed<string>(() => {

    const dates = this.$impactDates();

    if (!this.$isSkip()) return `Adds 1 extra service: ${formatLongDate(dates[0])}`;
    if (dates.length === 0) return '';

    return `Skips ${dates.length} planned ${dates.length === 1 ? 'service' : 'services'}: ${joinDates(dates)}`;
  });

  /** Extra mode always has an impact worth stating; Skip mode hides the row when nothing is affected. */
  protected readonly $showImpact = computed(() => this.$impactNote().length > 0);

  public constructor() {

    DesignSystem.Mount(this.document, exceptionPopoverStyleId, exceptionPopoverBridgeStyles);
  }

  protected onRangeChange(range: CalendarRange): void {

    this.$range.set(range);
  }

  protected onDateChange(date: CalendarDate): void {

    this.$date.set(date);
  }

  protected onNoteInput(event: Event): void {

    this.$note.set((event.target as HTMLInputElement).value);
  }

  protected onCancel(): void {

    this.cancelled.emit();
  }

  /**
   * A range whose endpoints are the same day is emitted as a single-date skip, not a one-day range —
   * the engine treats both identically, and the narrower shape reads better wherever the exception is
   * later listed back to the user.
   */
  protected onConfirm(): void {

    const note = this.$note().trim();

    if (!this.$isSkip()) {
      this.changed.emit({ type: 'extraDate', date: this.$date(), ...(note ? { note } : {}) });
      return;
    }

    const range = this.$range();
    const from = range.from ?? this.today;
    const to = range.to ?? from;

    if (from === to) {
      this.changed.emit({ type: 'skipDate', date: from, ...(note ? { reason: note } : {}) });
      return;
    }

    this.changed.emit({ type: 'skipRange', from, to, ...(note ? { reason: note } : {}) });
  }
}
